import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyFieldDateTimeComponent } from './form/controls/date-time-type/date-time-type.component';
import { FormlyFieldFileComponent } from './form/controls/file-upload/file-upload.component';
import { MultiLineTextComponent } from './form/controls/multi-line-text/multi-line-text.component';
import { FormlyFieldMultiSelectListComponent } from './form/controls/multi-select-list/multi-select-list.component';
import { FormlyFieldObjectChooserFieldComponent } from './form/controls/object-chooser-field/object-chooser-field.component';
import { FormlyFieldRichTextComponent } from './form/controls/rich-text-type/rich-text-type.component';
import { FormlyFieldSectionHeaderComponent } from './form/controls/section-header/section-header.component';
import { FormlyFieldSectionWrapperComponent } from './form/controls/section-wrapper/section-wrapper.component';
import { FormlyFieldTextContentComponent } from './form/controls/text-content/text-content.component';
import {
  PhoneValidator,
  WebAddressValidator,
  maxValueError,
  minValueError,
  maxLengthError,
  minLengthError,
} from './form/validators';
import { AcknowledgmentScreenComponent } from './form/acknowledgment-screen/acknowledgment-screen.component';
import { PageNotFoundComponent } from './form/page-not-found/page-not-found.component';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDatetimepickerModule } from '@mat-datetimepicker/core';
import { MatMomentDatetimeModule } from '@mat-datetimepicker/moment';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { FormlyMatFormFieldModule } from '@ngx-formly/material/form-field';
import { JoditAngularModule } from 'jodit-angular';
import { NgxCaptchaModule } from 'ngx-captcha';
import { NgxUploaderModule } from 'ngx-uploader';
import { SanitizeHtmlModule } from './form/pipes/sanitize-html/sanitize.module';
import { PipesModule } from './shared/pipes/pipes.module';
import { FormComponent } from './form/form.component';
import { get } from 'lodash';
import { ContactSelfServiceFormsModule } from './form/contact-self-service-forms/contact-self-service-forms.module';
import { ManualFocusModule } from './form/directives/manual-focus/manual-focus.module';
import { MatCheckboxModule } from '@angular/material/checkbox';

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(
    httpClient,
    `${environment.assetPathWithoutOrigin}i18n/`,
    '.json'
  );
}
@NgModule({
  declarations: [
    AppComponent,
    FormComponent,
    FormlyFieldFileComponent,
    FormlyFieldRichTextComponent,
    FormlyFieldDateTimeComponent,
    FormlyFieldRichTextComponent,
    FormlyFieldDateTimeComponent,
    FormlyFieldTextContentComponent,
    FormlyFieldMultiSelectListComponent,
    FormlyFieldObjectChooserFieldComponent,
    FormlyFieldSectionHeaderComponent,
    AcknowledgmentScreenComponent,
    MultiLineTextComponent,
    PageNotFoundComponent,
    FormlyFieldSectionWrapperComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    CommonModule,
    ReactiveFormsModule,
    FormlyMatFormFieldModule,
    FormlyMaterialModule,
    MatButtonModule,
    MatTabsModule,
    MatCardModule,
    MatExpansionModule,
    FormlyMatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    MatDatetimepickerModule,
    MatMomentDatetimeModule,
    HttpClientModule,
    MatSnackBarModule,
    NgxCaptchaModule,
    JoditAngularModule,
    NgxUploaderModule,
    MatListModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatToolbarModule,
    MatCheckboxModule,
    SanitizeHtmlModule,
    PipesModule,
    FormsModule,
    ContactSelfServiceFormsModule,
    ManualFocusModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    FormlyModule.forRoot({
      types: [
        {
          name: 'file-upload',
          component: FormlyFieldFileComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'rich-text',
          component: FormlyFieldRichTextComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'date-time',
          component: FormlyFieldDateTimeComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'text-content',
          component: FormlyFieldTextContentComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'list-items-select',
          component: FormlyFieldMultiSelectListComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'object-chooser',
          component: FormlyFieldObjectChooserFieldComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'section-header',
          component: FormlyFieldSectionHeaderComponent,
        },
        {
          name: 'multi-line-text',
          component: MultiLineTextComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'checkbox',
          extends: 'input',
          defaultOptions: {
            templateOptions: {
              type: 'checkbox',
            },
            expressionProperties: {
              'templateOptions.disabled': (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ) => {
                return field.templateOptions.readonly;
              },
            },
          },
        },
        {
          name: 'number',
          extends: 'input',
          defaultOptions: {
            templateOptions: {
              type: 'number',
            },
            expressionProperties: {
              'templateOptions.min': (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ) => {
                return get(field, 'objectSchemaProperties.minimum', null);
              },
              'templateOptions.max': (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ) => {
                return get(field, 'objectSchemaProperties.maximum', null);
              },
            },
          },
        },
        {
          name: 'phone-number',
          extends: 'input',
          defaultOptions: {
            validators: {
              validation: ['phone-number'],
            },
          },
        },
        {
          name: 'email-address',
          extends: 'input',
          defaultOptions: {
            validators: {
              validation: ['email'],
            },
          },
        },
        {
          name: 'web-address',
          extends: 'input',
          defaultOptions: {
            validators: {
              validation: ['webAddress'],
            },
          },
        },
      ],
      wrappers: [
        {
          name: 'noggin-section',
          component: FormlyFieldSectionWrapperComponent,
        },
      ],
      validators: [
        { name: 'phone-number', validation: PhoneValidator },
        { name: 'email', validation: Validators.email },
        { name: 'webAddress', validation: WebAddressValidator },
      ],
      validationMessages: [
        {
          name: 'phone',
          message:
            'Phone numbers must start with a + followed by the country code and only numbers or spaces',
        },
        { name: 'email', message: 'Must be a valid email address' },
        { name: 'required', message: 'This field is required' },
        {
          name: 'webAddress',
          message:
            'Web address must be valid and start with http:// or https://',
        },
        {
          name: 'futureDateTime',
          message: 'Invalid date, select only past date and time',
        },
        {
          name: 'pastDateTime',
          message: 'Invalid date, select only future date and time',
        },
        {
          name: 'futureDate',
          message: 'Invalid date, select only past date',
        },
        {
          name: 'pastDate',
          message: 'Invalid date, select only future date',
        },
        { name: 'max', message: maxValueError },
        { name: 'min', message: minValueError },
        { name: 'maxlength', message: maxLengthError },
        { name: 'minlength', message: minLengthError },
      ],
    }),
  ],
  exports: [
    FormlyFieldRichTextComponent,
    FormlyFieldDateTimeComponent,
    FormlyFieldRichTextComponent,
    FormlyFieldTextContentComponent,
    FormlyFieldMultiSelectListComponent,
    FormlyFieldObjectChooserFieldComponent,
    FormlyFieldSectionHeaderComponent,
    AcknowledgmentScreenComponent,
    MultiLineTextComponent,
    PageNotFoundComponent,
    FormlyFieldSectionWrapperComponent,
  ],
  providers: [{ provide: MAT_DATE_LOCALE, useValue: 'en-AU' }],
  bootstrap: [AppComponent],
})
export class AppModule {}
